import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { CopyableReadonlyField } from "./CopyableReadonlyField";
import { ProviderIcon } from "./ProviderIcon";
import { SsoProviderStatus } from "@/types/sso";

interface SsoProviderCardProps {
  provider: SsoProviderStatus;
  onConfigure: () => void;
  onRemove: () => void;
  authorizedOrigin?: string;
  canWrite: boolean;
}

export const SsoProviderCard: React.FC<SsoProviderCardProps> = ({
  provider,
  authorizedOrigin,
  onConfigure,
  onRemove,
  canWrite,
}) => {
  const { t } = useTranslation();
  const providerLabel = t(`sso.providers.${provider.alias}`);

  const statusBadge = renderStatusBadge(provider, t);

  return (
    <Card>
      <CardContent className="flex flex-col gap-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <ProviderIcon alias={provider.alias} />
            <div className="flex min-w-0 flex-col gap-1">
              <span className="text-foreground text-base leading-tight font-semibold">
                {providerLabel}
              </span>
              {statusBadge}
            </div>
          </div>

          {canWrite ? (
            <div className="flex shrink-0 items-center gap-2">
              {provider.configured ? (
                <Button
                  type="button"
                  variant="new_outline"
                  size="sm"
                  onClick={onRemove}
                >
                  {t("sso.card.remove")}
                </Button>
              ) : null}
              <Button
                type="button"
                variant={provider.configured ? "new_secondary" : "new_default"}
                size="sm"
                onClick={onConfigure}
              >
                {provider.configured
                  ? t("sso.card.reconfigure")
                  : t("sso.card.configure")}
              </Button>
            </div>
          ) : null}
        </div>

        {provider.configured && (
          <div className="flex flex-col gap-1">
            <span className="text-muted-foreground text-xs font-medium">
              {t("sso.card.clientIdLabel")}
            </span>
            <span className="text-foreground font-mono text-sm">
              {provider.clientId ?? t("sso.card.clientIdEmpty")}
            </span>
          </div>
        )}

        <div className="flex flex-col gap-3 border-t pt-4">
          <CopyableReadonlyField
            id={`sso-${provider.alias}-redirect`}
            label={t("sso.card.redirectUrlLabel")}
            value={provider.redirectUrl}
            helpText={t("sso.card.redirectUrlHelp")}
          />

          {provider.alias === "google" && authorizedOrigin && (
            <CopyableReadonlyField
              id="sso-google-origin"
              label={t("sso.card.authorizedOriginLabel")}
              value={authorizedOrigin}
              helpText={t("sso.card.authorizedOriginHelp")}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

function renderStatusBadge(
  provider: SsoProviderStatus,
  t: (key: string) => string,
): React.ReactNode {
  if (!provider.configured) {
    return (
      <Badge
        variant="outline"
        className="border-border bg-muted/40 text-muted-foreground w-fit"
      >
        {t("sso.status.notConfigured")}
      </Badge>
    );
  }
  if (!provider.enabled) {
    return (
      <Badge
        variant="outline"
        className="border-border bg-muted/40 text-muted-foreground w-fit"
      >
        {t("sso.status.disabled")}
      </Badge>
    );
  }
  return (
    <Badge variant="success" className="w-fit">
      {t("sso.status.active")}
    </Badge>
  );
}
